/**
 * People — the index, and one person's page.
 *
 * The page is the only view that is about a person rather than about a pile of
 * photographs, so it is where the archive's derived facts surface: a lifespan,
 * a decade-by-decade count, who they are related to and how, what was said
 * about them, and where they appear on film.
 *
 * The appearance strip is the reason `appearances[]` exists. A face matched in
 * a film is not a photograph — it is an interval, and the useful thing to do
 * with it is jump the player to the frame where the match was strongest. Each
 * button seeks the video above it to `bestFrameMs`.
 */

import { h, patch, replace, svg } from "../dom";
import { api, blobUrl, cropUrl } from "../api";
import { icon } from "../icons";
import { store } from "../state";
import { ask } from "../modal";
import { empty, face, failure, fingerprint, loading, toast } from "../ui";
import { count, formatDate, lifespan, personName, timecode, duration } from "../format";
import { videoPlayer } from "../videoplayer";
import type { Appearance, PersonPage, PersonSummary } from "../types";
import type { RefreshOptions, View } from "./view";

export function mount(pane: HTMLElement): View {
  let generation = 0;
  let identifyDialog: HTMLDialogElement | null = null;

  async function nameOrMerge(identityId: string, name: string): Promise<string> {
    const people = await api.people(store.libraryId);
    const existing = people.find(
      (person) =>
        person.identityId !== identityId &&
        person.name?.trim().localeCompare(name, undefined, { sensitivity: "accent" }) === 0,
    );
    if (existing && store.can("identity.merge")) {
      const answer = await ask({
        title: `Another ${name} already exists`,
        body: "Choose whether these face groups are the same person or two people who share a name.",
        fields: [{
          name: "choice",
          label: "Keep as",
          value: "separate",
          options: [["separate", "Two different people"], ["merge", "The same person"]],
        }],
        confirm: "Continue",
      });
      if (!answer) throw new Error("Naming cancelled");
      if (answer.choice === "merge") {
        await api.mergeIdentities(store.libraryId, existing.identityId, [identityId]);
        return existing.identityId;
      }
    }
    await api.nameIdentity(store.libraryId, identityId, name);
    return identityId;
  }

  function closeIdentify(): void {
    identifyDialog?.close();
    identifyDialog?.remove();
    identifyDialog = null;
  }

  async function identifyPeople(groups: PersonSummary[], start = 0): Promise<void> {
    closeIdentify();
    if (!groups.length) return;

    let index = Math.max(0, Math.min(start, groups.length - 1));
    const dialog = h("dialog", { class: "modal identify-people" }) as HTMLDialogElement;
    identifyDialog = dialog;

    const finish = (): void => {
      closeIdentify();
      void refresh({ quiet: true });
    };
    const advance = (): void => {
      if (index + 1 >= groups.length) {
        finish();
        return;
      }
      index++;
      void draw();
    };

    const draw = async (): Promise<void> => {
      const group = groups[index]!;
      replace(dialog, h("div", { class: "modal-body" }, loading("face group")));
      let person: PersonPage;
      try {
        person = await api.person(store.libraryId, group.identityId);
      } catch (error) {
        replace(dialog, h("div", { class: "modal-body" }, failure(error, () => void draw())));
        return;
      }
      if (identifyDialog !== dialog) return;

      const name = h("input", {
        class: "identify-name",
        type: "text",
        placeholder: "Type their name",
        "aria-label": "This person's name",
        maxlength: "80",
        value: person.name ?? "",
      }) as HTMLInputElement;
      const save = h("button", { class: "btn primary", type: "button", text: "Name and continue" });
      const setBusy = (busy: boolean): void => {
        for (const control of dialog.querySelectorAll<HTMLButtonElement | HTMLInputElement>("button,input")) {
          control.disabled = busy;
        }
      };
      const saveName = async (): Promise<void> => {
        const value = name.value.trim();
        if (!value) {
          name.focus();
          return;
        }
        setBusy(true);
        try {
          await nameOrMerge(group.identityId, value);
          toast(`Named ${value}.`);
          store.named();
          advance();
        } catch (error) {
          setBusy(false);
          toast(error instanceof Error ? error.message : "could not name them", true);
        }
      };
      save.addEventListener("click", () => void saveName());
      name.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          void saveName();
        }
      });

      const detailedCrops = person.crops.filter((crop) => cropUrl(crop.cropKey));
      const crops = detailedCrops.length
        ? detailedCrops
        : group.crops.map((cropKey, cropIndex) => ({
            id: `summary-${cropIndex}`,
            cropKey,
            quality: null,
            chosen: false,
            sourceAsset: null,
          }));
      replace(
        dialog,
        h(
          "div",
          { class: "modal-body" },
          h(
            "header",
            { class: "identify-head" },
            h("div", { class: "identify-progress mono", text: `${index + 1} of ${groups.length}` }),
            h("h2", { class: "modal-title", text: "Is this one person?" }),
            h("p", { class: "sub", text: `${count(group.faceCount, "face")} grouped by likeness. Check the faces, then name the person once.` }),
          ),
          h(
            "div",
            { class: "identify-faces", "aria-label": "Faces likely to be the same person" },
            ...crops.map((crop) => {
              const src = cropUrl(crop.cropKey);
              const preview = src
                ? h("img", { src, alt: "", loading: "lazy" })
                : h("div", { class: "identify-face-missing", text: "?" });
              return h(
                "figure",
                { class: "identify-face" },
                preview,
                crop.sourceAsset && store.can("identity.name")
                  ? h("button", {
                      type: "button",
                      title: "Not this person",
                      "aria-label": "Remove this face from the group",
                      onclick: () => {
                        setBusy(true);
                        void notThisFace(person, crop, false).then((changed) => {
                          if (changed) void draw();
                          else setBusy(false);
                        });
                      },
                    }, icon("close", "i sm"))
                  : null,
              );
            }),
          ),
          h("label", { class: "modal-label", text: "Who is this?" }),
          name,
          h(
            "div",
            { class: "identify-actions" },
            index > 0
              ? h("button", { class: "btn ghost", type: "button", text: "Previous", onclick: () => { index--; void draw(); } })
              : null,
            h("button", { class: "btn ghost", type: "button", text: "Skip", onclick: advance }),
            store.can("identity.name")
              ? h("button", {
                  class: "btn ghost danger",
                  type: "button",
                  text: "Not a relative",
                  onclick: () => {
                    setBusy(true);
                    void api.setStranger(store.libraryId, group.identityId, true)
                      .then(() => {
                        toast("Taken off the People screen.");
                        advance();
                      })
                      .catch((error: unknown) => {
                        setBusy(false);
                        toast(error instanceof Error ? error.message : "could not do that", true);
                      });
                  },
                })
              : null,
            h("div", { class: "grow" }),
            h("button", { class: "btn ghost", type: "button", text: "Close", onclick: finish }),
            save,
          ),
        ),
      );
      name.focus();
    };

    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      finish();
    });
    document.body.appendChild(dialog);
    dialog.showModal();
    await draw();
  }

  // --- the index ----------------------------------------------------------

  /**
   * The list as last drawn.
   *
   * Read at click time rather than captured: a live update keeps every card
   * that did not change, and a kept card's listener would otherwise walk
   * through the unnamed people as they were when it was first drawn.
   */
  let latest: PersonSummary[] = [];
  const unnamedNow = (): PersonSummary[] => latest.filter((x) => !x.name);

  function indexEl(people: PersonSummary[]): HTMLElement {
    latest = people;
    const named = people.filter((p) => p.name);
    const unnamed = people.filter((p) => !p.name);

    const card = (p: PersonSummary, index: number): HTMLElement => {
      const open = (): void => {
        if (!p.name && store.can("identity.name")) {
          const queue = unnamedNow();
          void identifyPeople(
            queue,
            Math.max(0, queue.findIndex((x) => x.identityId === p.identityId)),
          );
        } else {
          store.go("people", { personId: p.identityId });
        }
      };
      const crops = p.crops.length ? p.crops : p.coverCrop ? [p.coverCrop] : [];
      const group = h(
        "button",
        { class: "persongroup", type: "button", title: "Review this face group", onclick: open },
        face(p.name, crops[0] ?? null, "face groupcover"),
        h(
          "span",
          { class: "facestrip" },
          ...crops.slice(1, 6).map((crop) => face(p.name, crop, "groupface")),
        ),
      );
      const label = p.name
        ? h("button", { class: "personname", type: "button", text: p.name, onclick: open })
        : store.can("identity.name")
          ? h("input", {
              class: "personnameinput",
              placeholder: `Person ${index + 1}`,
              "aria-label": `Name person ${index + 1}`,
              onchange: (event: Event) => {
                const input = event.target as HTMLInputElement;
                const name = input.value.trim();
                if (!name) return;
                input.disabled = true;
                void nameOrMerge(p.identityId, name)
                  .then((identityId) => {
                    toast(identityId === p.identityId ? `Named ${name}.` : `Added these faces to ${name}.`);
                    store.named();
                    void refresh({ quiet: true });
                  })
                  .catch((error: unknown) => {
                    input.disabled = false;
                    toast(error instanceof Error ? error.message : "could not name them", true);
                  });
              },
            })
          : h("button", { class: "personname", type: "button", text: personName(null), onclick: open });

      return h(
        "div",
        {
          class: p.name ? "personcard" : "personcard unnamed",
          "data-key": p.identityId,
          "data-sig": fingerprint([p, index]),
        },
        group,
        label,
        h("button", {
          class: "personcount mono",
          type: "button",
          text: count(p.faceCount, "face"),
          title: "Review every face in this group",
          onclick: open,
        }),
      );
    };

    const wrap = h("div", null);
    wrap.appendChild(
      h(
        "div",
        { class: "viewhead" },
        h("h2", { text: "People" }),
        h("div", { class: "grow" }),
        h("button", {
          class: "btn sm ghost",
          text: "Family tree",
          onclick: () => store.go("tree"),
        }),
        unnamed.length && store.can("identity.name")
          ? h("button", {
              class: "btn sm primary",
              text: `Identify ${count(unnamed.length, "person")}`,
              onclick: () => void identifyPeople(unnamedNow()),
            })
          : null,
        store.can("asset.add")
          ? h("button", {
              class: "btn sm",
              text: "Find and group people",
              title: "Look for unidentified faces and group photographs of the same person",
              onclick: () => window.dispatchEvent(new Event("heirloom:process-people")),
            })
          : null,
      ),
    );
    wrap.appendChild(
      h("p", {
        class: "sub",
        text: "Everybody the archive has a face for. A person with no name is still a person — name them and every photograph they are in gains their name at once.",
      }),
    );
    if (unnamed.length) {
      wrap.appendChild(h("div", { class: "sectlbl", text: "People to identify" }));
      wrap.appendChild(h("div", { class: "faces" }, ...unnamed.map(card)));
      wrap.appendChild(
        h(
          "div",
          { class: "note" },
          h("span", {
            text: `${count(unnamed.length, "person")} waiting for a name. Open one to review the grouped faces and name them all at once.`,
          }),
        ),
      );
    }
    if (named.length) {
      if (unnamed.length) wrap.appendChild(h("div", { class: "sectlbl", text: "Named people" }));
      wrap.appendChild(h("div", { class: "faces" }, ...named.map(card)));
    }
    return wrap;
  }

  // --- one person ---------------------------------------------------------

  /** Counts by decade, zero-filled, so a gap in someone's life is visible. */
  function sparkline(over: PersonPage["overTime"]): SVGSVGElement {
    const known = over.filter((d) => /^\d{4}s$/.test(d.decade));
    if (!known.length) return svg("svg", { class: "spark", width: "0", height: "0" });
    const years = known.map((d) => Number(d.decade.slice(0, 4)));
    const first = Math.min(...years);
    const last = Math.max(...years);
    const cols: Array<{ decade: number; n: number }> = [];
    for (let y = first; y <= last; y += 10) {
      cols.push({ decade: y, n: known.find((k) => Number(k.decade.slice(0, 4)) === y)?.count ?? 0 });
    }
    const max = Math.max(1, ...cols.map((c) => c.n));
    const BW = 22;
    const GAP = 6;
    const H = 54;
    const w = cols.length * (BW + GAP);
    const root = svg("svg", {
      class: "spark",
      width: String(w),
      height: String(H + 16),
      viewBox: `0 0 ${w} ${H + 16}`,
    });
    cols.forEach((c, i) => {
      const bh = c.n ? Math.max(3, Math.round((H - 12) * (c.n / max))) : 2;
      const x = i * (BW + GAP);
      root.appendChild(
        svg("rect", {
          class: c.n ? "" : "zero",
          x: String(x),
          y: String(H - bh),
          width: String(BW),
          height: String(bh),
          rx: "2",
        }),
      );
      if (c.n) {
        root.appendChild(svg("text", { x: String(x + BW / 2), y: String(H - bh - 3), "text-anchor": "middle" }, String(c.n)));
      }
      root.appendChild(
        svg("text", { x: String(x + BW / 2), y: String(H + 12), "text-anchor": "middle" }, `${String(c.decade).slice(2)}s`),
      );
    });
    return root;
  }

  /** The player plus its seek targets. One video element, many jump points. */
  function appearanceStrip(p: PersonPage): HTMLElement | null {
    if (!p.appearances.length) return null;

    const byAsset = new Map<string, Appearance[]>();
    for (const a of p.appearances) {
      const at = byAsset.get(a.assetId);
      if (at) at.push(a);
      else byAsset.set(a.assetId, [a]);
    }

    const player = videoPlayer({ className: "player" });
    const video = player.video;
    const readout = h("div", { class: "seekat mono", text: "Pick a moment below." });
    const strip = h("div", { class: "strip" });
    let current: HTMLElement | null = null;

    const seek = (a: Appearance, btn: HTMLElement): void => {
      const src = blobUrl(a.contentHash);
      if (src && video.getAttribute("src") !== src) {
        player.setSource(src, src);
      }
      const to = a.bestFrameMs / 1000;
      const jump = (): void => {
        try {
          video.currentTime = to;
        } catch {
          /* metadata not in yet; the loadedmetadata handler will do it */
        }
      };
      if (video.readyState >= 1) jump();
      else video.addEventListener("loadedmetadata", jump, { once: true });
      current?.classList.remove("on");
      btn.classList.add("on");
      current = btn;
      readout.textContent = `${a.fileName} · seeking to ${timecode(a.bestFrameMs)} of ${duration(a.durationMs) || "?"}`;
    };

    for (const [assetId, runs] of byAsset) {
      const first = runs[0]!;
      strip.appendChild(
        h("div", { class: "striplbl mono", text: first.fileName, title: assetId }),
      );
      for (const a of runs) {
        const btn = h(
          "button",
          { class: "", type: "button" },
          h("span", { class: "t", text: timecode(a.bestFrameMs) }),
          h("span", { class: "f", text: `${timecode(a.startMs)}–${timecode(a.endMs)}` }),
          h("span", { class: "len", text: `${Math.round((a.endMs - a.startMs) / 1000)}s on screen` }),
        );
        btn.addEventListener("click", () => seek(a, btn));
        strip.appendChild(btn);
      }
    }

    return h(
      "div",
      { class: "dg" },
      h("h4", null, `On film — ${count(byAsset.size, "reel")}, ${count(p.appearances.length, "appearance")}`),
      player.el,
      readout,
      strip,
    );
  }

  /**
   * The faces this person could be shown by.
   *
   * The automatic pick — their highest-scoring crop — is usually fine and
   * occasionally wrong in a way no measurement catches: the sharpest
   * photograph of somebody can be the one where they are squinting, or the one
   * at eighty when the family pictures them at thirty. So this is an override,
   * and choosing the one already chosen clears it back to automatic.
   */
  /**
   * Saying the archive is wrong about somebody.
   *
   * These are the three corrections a face pass makes necessary and could not
   * previously be made: two entries that are one person, somebody who is not a
   * person the family knows, and a group that should never have existed. A
   * pass that can only ever add people produces an archive that gets worse
   * with use — a group holding three different people stays that way, and
   * every later run matches new faces against the contaminated set.
   *
   * Merging and forgetting need `identity.merge` rather than `identity.name`,
   * because they change what everybody sees: a merge deletes an entry from the
   * family's archive and a forget takes somebody off the People screen for
   * every member.
   */
  function corrections(p: PersonPage): HTMLElement[] {
    const out: HTMLElement[] = [];

    if (store.can("identity.merge")) {
      out.push(
        h("button", {
          class: "btn",
          text: "Same as…",
          title: "Two entries that are one person — move this one's photographs into another",
          onclick: () => void mergeInto(p),
        }),
      );
    }

    if (store.can("identity.name")) {
      out.push(
        h("button", {
          class: "btn ghost",
          text: "Not a relative",
          title:
            "A passer-by who wandered into the frame. They come off the People " +
            "screen and their faces stay put, so a later pass does not invent " +
            "them again.",
          onclick: () => {
            void (async () => {
              try {
                await api.setStranger(store.libraryId, p.identityId, true);
                toast("Taken off the People screen.");
                store.go("people", { personId: null });
              } catch (e) {
                toast(e instanceof Error ? e.message : "could not do that", true);
              }
            })();
          },
        }),
      );
    }

    if (store.can("identity.merge")) {
      out.push(
        h("button", {
          class: "btn ghost danger",
          text: "Forget them",
          title: "Break this group up — every face in it goes back to being unnamed",
          onclick: () => void forget(p),
        }),
      );
    }
    return out;
  }

  /**
   * Merge this person into another.
   *
   * The direction matters and is easy to get backwards, so the wording says
   * which entry survives rather than leaving it to be discovered: this one's
   * photographs move, and the one you pick is the one that remains.
   */
  async function mergeInto(p: PersonPage): Promise<void> {
    let others: PersonSummary[];
    try {
      others = (await api.people(store.libraryId)).filter(
        (x) => x.identityId !== p.identityId,
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : "could not load the others", true);
      return;
    }
    if (!others.length) {
      toast("There is nobody else in this archive to merge with.");
      return;
    }

    const answer = await ask({
      title: `Who is ${personName(p.name)} the same as?`,
      body:
        "Everything on this entry — photographs, faces, relationships — moves " +
        "to the person you pick, and this entry goes. Rejections move too, so " +
        "a face taken off either of them stays off.",
      fields: [
        {
          name: "into",
          label: "The entry that stays",
          options: others.map((o) => [
            o.identityId,
            `${personName(o.name)} · ${count(o.count, "photograph")}`,
          ]),
        },
      ],
      confirm: "Merge them",
      destructive: true,
    });
    const into = answer?.into;
    if (!into) return;

    try {
      const r = await api.mergeIdentities(store.libraryId, into, [p.identityId]);
      toast(`Merged. ${count(r.moved, "face")} moved.`);
      store.go("people", { personId: into });
    } catch (e) {
      toast(e instanceof Error ? e.message : "could not merge them", true);
    }
  }

  async function forget(p: PersonPage): Promise<void> {
    const answer = await ask({
      title: `Forget ${personName(p.name)}?`,
      body:
        "Every face in this group goes back to being an unnamed face, and the " +
        "group is remembered as wrong so a later pass does not rebuild it. " +
        "The photographs themselves are untouched.",
      fields: [],
      confirm: "Forget them",
      destructive: true,
    });
    if (!answer) return;
    try {
      const r = await api.forgetIdentity(store.libraryId, p.identityId);
      toast(`Forgotten. ${count(r.faces, "face")} went back to being unnamed.`);
      store.go("people", { personId: null });
    } catch (e) {
      toast(e instanceof Error ? e.message : "could not forget them", true);
    }
  }

  function facePicker(p: PersonPage): HTMLElement | null {
    if (!store.can("identity.name") || p.crops.length < 2) return null;
    const row = h("div", { class: "facepick" });
    for (const c of p.crops) {
      const src = cropUrl(c.cropKey);
      if (!src) continue;
      const holder = h("span", { class: "facepickone" });
      holder.appendChild(
        h(
          "button",
          {
            class: c.chosen ? "on" : "",
            title: c.chosen ? "Shown by this face — click to go back to automatic" : "Show them by this face",
            onclick: () => {
              void api
                .setPersonCover(store.libraryId, p.identityId, c.chosen ? null : c.id)
                .then(() => {
                  toast(c.chosen ? "Back to picking automatically." : "Face changed.");
                  void refresh({ quiet: true });
                })
                .catch((e: unknown) =>
                  toast(e instanceof Error ? e.message : "could not change it", true),
                );
            },
          },
          h("img", { src, alt: "", loading: "lazy" }),
        ),
      );

      // "That is not them", on the face itself. This is the screen where a
      // contaminated group is obvious — a strip of ten faces with three
      // different people in it — and it was the one screen with no way to say
      // so.
      holder.appendChild(
        h("button", {
          class: "facepickno",
          text: "×",
          title: "Not this person — take this face and its duplicates off them",
          onclick: () => void notThisFace(p, c),
        }),
      );
      row.appendChild(holder);
    }
    return row.children.length ? row : null;
  }

  /**
   * Take a face off this person, and the ones that are obviously the same face.
   *
   * The cascade is what makes this a repair rather than a chore. A group of
   * forty faces showing the same wrong person needed forty rejections; now one
   * rejection takes the near-identical ones with it. The comparison is between
   * face vectors and happens in the browser, because a 512-dimensional face
   * template never reaches the server.
   */
  async function notThisFace(
    p: PersonPage,
    crop: PersonPage["crops"][number],
    reload = true,
  ): Promise<boolean> {
    if (!crop.sourceAsset) {
      toast("That face is not linked to a photograph, so it cannot be moved.", true);
      return false;
    }
    try {
      const detail = await api.assetDetail(store.libraryId, crop.sourceAsset);
      const faces = detail.faces.filter((f) => f.identityId === p.identityId);
      if (!faces.length) {
        toast("That face is no longer on this person.", true);
        return false;
      }
      await api.rejectFaces(
        store.libraryId,
        p.identityId,
        faces.map((f) => f.id),
      );

      // Best-effort, and deliberately after the rejection has landed: a
      // cascade that fails must leave the correction somebody actually asked
      // for in place.
      let also = 0;
      try {
        const { cascade } = await import("../cascade");
        also = (await cascade(p, crop.cropKey)).also;
      } catch {
        /* the one face is off, which is what was asked */
      }
      toast(
        also
          ? `Taken off, along with ${count(also, "face")} that looked the same.`
          : "Taken off.",
      );
      if (reload) void refresh({ quiet: true });
      return true;
    } catch (e) {
      toast(e instanceof Error ? e.message : "could not change that", true);
      return false;
    }
  }

  function personEl(p: PersonPage): HTMLElement {
    const wrap = h("div", null);

    const head = h(
      "div",
      { class: "phead" },
      face(p.name, (p.crops.find((c) => c.chosen) ?? p.crops[0])?.cropKey ?? null, "face"),
      h(
        "div",
        { class: "who" },
        h("h2", { text: personName(p.name) }),
        h("div", { class: "life", text: lifespan(p.birthYear, p.deathYear) }),
        h("div", {
          class: "counts",
          // `/person` gives no precision for these two, so neither does the
          // label: `formatDate` with a null precision says the year and admits
          // it rather than picking the most precise reading of the string.
          text: `${count(p.photoCount, "photograph")} · ${count(p.videoCount, "film")} · seen ${formatDate(
            p.firstSeen,
            null,
            { short: true },
          )} to ${formatDate(p.lastSeen, null, { short: true })}`,
        }),
      ),
      h("div", { class: "grow" }),
      h(
        "div",
        { class: "pacts" },
        h("button", {
          class: "btn primary",
          text: "Show every photograph",
          onclick: () => {
            store.setFilter({ people: [p.identityId] });
            store.go("cards");
          },
        }),
        h("button", {
          class: "btn",
          text: "Place in the tree",
          onclick: () => store.go("tree", { treeRoot: p.identityId }),
        }),
        // Naming is a capability. A reader who cannot name people is not shown
        // a box that would 403 the moment they typed in it.
        !p.name && store.can("identity.name")
          ? h("button", {
              class: "btn",
              text: "Name this person",
              onclick: () => {
                void (async () => {
                  const answer = await ask({
                    title: "Who is this?",
                    body:
                      "The name is yours — another member of the family can " +
                      "call the same person something else. Naming them here " +
                      "names them in every photograph they appear in.",
                    // The face, so nobody is named from memory of a thumbnail
                    // they scrolled past.
                    preview: p.crops?.[0]
                      ? h("img", { src: cropUrl(p.crops[0].cropKey) ?? "", alt: "" })
                      : undefined,
                    fields: [
                      {
                        name: "name",
                        label: "Name",
                        placeholder: "Mary",
                        required: true,
                        maxLength: 80,
                      },
                    ],
                    confirm: "Name them",
                  });
                  const name = answer?.name?.trim();
                  if (!name) return;
                  try {
                    const identityId = await nameOrMerge(p.identityId, name);
                    toast(identityId === p.identityId ? `Named ${name}.` : `Added these faces to ${name}.`);
                    store.named();
                    if (identityId === p.identityId) void refresh({ quiet: true });
                    else store.go("people", { personId: identityId });
                  } catch (e) {
                    toast(e instanceof Error ? e.message : "could not name them", true);
                  }
                })();
              },
            })
          : null,
        ...corrections(p),
      ),
    );
    wrap.appendChild(
      h("button", {
        class: "btn sm ghost back",
        text: "← Everybody",
        onclick: () => store.go("people", { personId: null }),
      }),
    );
    wrap.appendChild(head);
    const faces = facePicker(p);
    if (faces) {
      wrap.appendChild(
        h(
          "div",
          { class: "facepick-wrap" },
          h("span", { class: "modal-label", text: "Shown by" }),
          faces,
        ),
      );
    }

    if (p.overTime.length) {
      wrap.appendChild(
        h(
          "div",
          { class: "dg" },
          h("h4", null, "Photographed, by decade"),
          sparkline(p.overTime),
        ),
      );
    }

    const strip = appearanceStrip(p);
    if (strip) wrap.appendChild(strip);

    const cols = h("div", { class: "cols" });

    const rel = h("div", { class: "dg" }, h("h4", null, `Related — ${p.relatives.length}`));
    if (p.relatives.length) {
      rel.appendChild(
        h(
          "div",
          { class: "rellist" },
          ...p.relatives.map((r) =>
            h(
              "button",
              { onclick: () => store.go("people", { personId: r.identityId }) },
              h("span", { text: personName(r.name) }),
              h("span", { class: "kin", text: r.kinship }),
            ),
          ),
        ),
      );
    } else {
      rel.appendChild(
        h("p", {
          class: "sub",
          text: "Nobody is recorded as related to them yet. Relationships are entered in the tree.",
        }),
      );
    }
    cols.appendChild(rel);

    const st = h("div", { class: "dg" }, h("h4", null, `Told about them — ${p.stories.length}`));
    if (p.stories.length) {
      for (const s of p.stories) {
        st.appendChild(
          h(
            "div",
            { class: "story" },
            h(
              "div",
              { class: "meta" },
              h("span", { class: "kind", text: s.kind }),
              h("span", { text: s.created_at.slice(0, 10) }),
            ),
            h("p", { text: s.body ?? "(a recording, not yet transcribed)" }),
          ),
        );
      }
    } else {
      st.appendChild(
        h("p", {
          class: "sub",
          text: "Nothing has been written down about them. Record something in the desktop app and it appears here.",
        }),
      );
    }
    cols.appendChild(st);

    wrap.appendChild(cols);
    return wrap;
  }

  // --- dispatch -----------------------------------------------------------

  /** What is on screen: which page, and the data it was drawn from. */
  let shown: { page: string; sig: string } | null = null;

  async function refresh(opts: RefreshOptions = {}): Promise<void> {
    generation++;
    const mine = generation;
    const page = store.route.personId ?? "";
    // In place only when the same page is already drawn. Anything else — a
    // first load, another person, an error panel — has nothing worth keeping.
    const quiet = Boolean(opts.quiet && shown?.page === page && pane.firstChild);
    if (!quiet) {
      shown = null;
      replace(pane, loading(page ? "this person" : "people"));
    }
    try {
      if (page) {
        const p = await api.person(store.libraryId, page);
        if (mine !== generation) return;
        const sig = fingerprint(p);
        if (quiet) {
          // Most live messages are about somebody else. Nothing to do.
          if (sig === shown?.sig) return;
          // A film somebody is watching is not redrawn out from under them.
          // The next message after it stops brings the page up to date.
          const playing = Array.from(pane.querySelectorAll("video")).some((v) => !v.paused);
          if (playing) return;
        }
        // Rebuilt whole rather than patched: its buttons hold this person's
        // crops by position, and a kept "not this face" button pointing at a
        // shifted list would reject the wrong face.
        const top = pane.scrollTop;
        replace(pane, personEl(p));
        pane.scrollTop = quiet ? top : 0;
        shown = { page, sig };
        return;
      }
      const people = await api.people(store.libraryId);
      if (mine !== generation) return;
      const sig = fingerprint(people);
      if (quiet && sig === shown?.sig) return;
      if (!people.length) {
        replace(
          pane,
          empty({
            icon: "people",
            title: "No faces found yet",
            body: [
              "Faces are detected on the device that holds the photographs, not here. Once a scan has been through the desktop app, everybody in it appears on this page waiting to be named.",
            ],
          }),
        );
        shown = { page, sig };
        return;
      }
      // Patched: a newly named person moves one card, not the whole screen.
      if (quiet) patch(pane, [indexEl(people)]);
      else replace(pane, indexEl(people));
      shown = { page, sig };
    } catch (e) {
      if (mine !== generation) return;
      // A failed live update leaves what is there. Only a load the reader
      // asked for is replaced by the error.
      if (quiet) return;
      replace(pane, failure(e, () => void refresh()));
    }
  }

  void refresh();

  return {
    refresh: (opts) => void refresh(opts),
    destroy() {
      generation++;
      closeIdentify();
    },
  };
}
